import { AllowedSystemEnum } from 'enums/allowedSystem.enum';
import jwtDecode from 'jwt-decode';
import { JwtToken } from 'model/types';
import { useCallback } from 'react';
import {
  getAuthorizationToken,
  getExpireIn,
  getRefreshTokenService,
  getScope,
  getTokenType,
} from 'services/config';
import { useAuth } from './useAuth';

const SSO_APPS_URL = process.env.REACT_APP_SSO_APPS ?? '';

const normalizeClaimArray = (value: string | string[] | undefined): string[] => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return [value];
};

const buildOauthHandoffUrl = (baseUrl: string): string => {
  const params = new URLSearchParams({
    accessToken: getAuthorizationToken(),
    refreshToken: getRefreshTokenService(),
    tokenType: getTokenType(),
    expiresIn: getExpireIn(),
    scope: getScope(),
  });

  return `${baseUrl}oauth/?${params.toString()}`;
};

export const useAppLinkAccess = () => {
  const { isAuthenticated } = useAuth();

  const resolveAppUrl = useCallback(
    (allowedSystem: AllowedSystemEnum, baseUrl?: string): string => {
      if (!isAuthenticated || !baseUrl) return SSO_APPS_URL;

      const token = getAuthorizationToken();
      if (!token) return SSO_APPS_URL;

      try {
        const { veiling_claim_type_allowed_systems: allowedSystems } =
          jwtDecode<JwtToken>(token);
        const hasAccess = normalizeClaimArray(allowedSystems).includes(
          String(allowedSystem),
        );
        return hasAccess ? buildOauthHandoffUrl(baseUrl) : SSO_APPS_URL;
      } catch {
        return SSO_APPS_URL;
      }
    },
    [isAuthenticated],
  );

  return { resolveAppUrl };
};
